import { AnyAction, combineReducers, configureStore } from '@reduxjs/toolkit';
import authReducer from './authSlice';
import jobsReducer from './jobsSlice';
import messagesReducer from './messagesSlice';
import paymentsReducer from './paymentsSlice';
import ratingsReducer from './ratingsSlice';
import applicationsReducer from './applicationsSlice';
import workerProfileReducer from './workerProfileSlice';
import scrapedJobsReducer from './scrapedJobsSlice';
import matchesReducer from './matchesSlice';
import autoApplicationsReducer from './autoApplicationsSlice';
import subscriptionReducer from './subscriptionSlice';
import employerReducer from './employerSlice';
import notificationsReducer from './notificationsSlice';
import insightsReducer from './insightsSlice';
import referralReducer from './referralSlice';
import scheduleReducer from './scheduleSlice';
import savedJobsReducer from './savedJobsSlice';
import profileViewsReducer from './profileViewsSlice';
import { createSessionBoundaryMiddleware } from './sessionBoundaryMiddleware';

export const appReducer = combineReducers({
  auth: authReducer,
  jobs: jobsReducer,
  messages: messagesReducer,
  payments: paymentsReducer,
  ratings: ratingsReducer,
  applications: applicationsReducer,
  workerProfile: workerProfileReducer,
  scrapedJobs: scrapedJobsReducer,
  matches: matchesReducer,
  autoApplications: autoApplicationsReducer,
  subscription: subscriptionReducer,
  employer: employerReducer,
  notifications: notificationsReducer,
  insights: insightsReducer,
  referrals: referralReducer,
  schedule: scheduleReducer,
  savedJobs: savedJobsReducer,
  profileViews: profileViewsReducer,
});

export const rootReducer = (
  state: ReturnType<typeof appReducer> | undefined,
  action: AnyAction,
) => {
  // Session replacement is a privacy boundary, not just an auth-state change.
  // Reset every slice so another account using the same tab cannot see cached
  // messages, payments, applications, matches, saved jobs, or employer data.
  const replacesSession = [
    'auth/logout/fulfilled',
    'auth/login/fulfilled',
    'auth/register/fulfilled',
  ].includes(action.type);
  const nextState = replacesSession ? undefined : state;
  return appReducer(nextState, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware => (
    getDefaultMiddleware().concat(createSessionBoundaryMiddleware())
  ),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;
